import { useCallback, useEffect, useState } from 'react'

import { api } from '../api'
import { wipeLocal } from '../lib/db'
import {
  forgetLocalPin,
  localFails,
  registerLocalFail,
  setLocalPin,
  unlockLocal,
} from '../lib/vault'
import Keypad from './Keypad'

const UID_KEY = 'device-uid'
const HAS_PIN = 'device-has-pin'
const WHO_KEY = 'device-who'

/** Постоянный номер устройства. Живёт до переустановки — этого достаточно:
 *  сервер держит код именно за этим номером, а не за учётной записью. */
export function deviceUid(): string {
  let uid: string | null = null
  try {
    uid = localStorage.getItem(UID_KEY)
  } catch {
    // приватный режим: номер проживёт один запуск, вход по коду не сработает
  }
  if (!uid) {
    uid = crypto.randomUUID()
    try {
      localStorage.setItem(UID_KEY, uid)
    } catch {
      /* пусто */
    }
  }
  return uid
}

export function hasPin(): boolean {
  try {
    return localStorage.getItem(HAS_PIN) === '1'
  } catch {
    return false
  }
}

export function rememberPinSet(on: boolean): void {
  try {
    if (on) localStorage.setItem(HAS_PIN, '1')
    else {
      localStorage.removeItem(HAS_PIN)
      localStorage.removeItem(WHO_KEY)
    }
  } catch {
    /* пусто */
  }
  if (!on) forgetLocalPin()
}

/** Столько попыток даётся без связи. Серверный счётчик в подвале недоступен,
 *  а без ограничения пятизначный код подбирается за вечер. */
const MAX_LOCAL_FAILS = 5

/** Имя владельца телефона: показываем на экране кода, как в банковских
 *  приложениях. Это не данные учётной записи, а подпись — чей это телефон. */
export function rememberWho(fullName: string): void {
  try {
    localStorage.setItem(WHO_KEY, fullName)
  } catch {
    /* пусто */
  }
}

function who(): { greeting: string; name: string | null; initials: string } {
  let full: string | null = null
  try {
    full = localStorage.getItem(WHO_KEY)
  } catch {
    /* пусто */
  }
  const h = new Date().getHours()
  const greeting =
    h < 5 ? 'Доброй ночи,' : h < 12 ? 'Доброе утро,' : h < 18 ? 'Добрый день,' : 'Добрый вечер,'
  if (!full) return { greeting: 'Учёт времени', name: null, initials: 'БЭ' }
  const parts = full.trim().split(/\s+/)
  return {
    greeting,
    // Имена в справочнике записаны как «Фамилия И.О.» — по частям их не
    // разложить, поэтому показываем как есть. Ошибиться тут хуже, чем быть
    // формальным: чужое имя на своём телефоне сразу настораживает.
    name: full,
    initials: ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase(),
  }
}

/**
 * Ввод кода при запуске.
 *
 * Пять ошибок — и устройство отвязывается, а локальные данные стираются: на
 * телефоне лежат пароли клиентов, и подбор кода не должен быть возможен.
 * Счётчик держит сервер, поэтому переустановка приложения его не сбросит.
 */
export default function PinLock({ onUnlocked, onForget }: {
  /** Токен с сервера или null, если код проверен на устройстве и берётся
   *  сохранённый токен. */
  onUnlocked: (token: string | null) => void
  onForget: () => void
}) {
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const me = who()

  /** Исчерпаны попытки: стираем всё локальное и отправляем входить паролем. */
  const giveUp = useCallback(
    async (message: string) => {
      await wipeLocal()
      rememberPinSet(false)
      setError(message)
      onForget()
    },
    [onForget],
  )

  const submit = useCallback(
    async (code: string) => {
      setBusy(true)
      setError(null)

      // Ключ к зашифрованному кэшу выводится из кода, поэтому проверяем его на
      // устройстве в любом случае — и когда связь есть, и когда её нет.
      const localOk = await unlockLocal(code)

      if (!navigator.onLine) {
        setPin('')
        if (localOk) onUnlocked(null)
        else {
          const fails = registerLocalFail()
          if (fails >= MAX_LOCAL_FAILS) {
            await giveUp('Слишком много попыток. Войдите паролем, когда будет связь.')
          } else {
            setError(`Неверный код. Осталось попыток: ${MAX_LOCAL_FAILS - fails}`)
          }
        }
        setBusy(false)
        return
      }

      try {
        const res = await api.loginByPin(deviceUid(), code)
        onUnlocked(res.access_token)
      } catch (e: any) {
        setPin('')
        if (e?.status === 423) {
          // Устройство заблокировано сервером: данные с него убираем.
          await giveUp('Слишком много попыток. Войдите паролем заново.')
        } else if (e?.status === 401) {
          registerLocalFail()
          setError('Неверный код')
        } else if (localOk) {
          // Сервер не ответил, но код на устройстве подошёл. В поле это
          // обычное дело: связь то есть, то нет.
          onUnlocked(null)
        } else {
          setError(e?.message ?? 'Не удалось проверить код')
        }
      } finally {
        setBusy(false)
      }
    },
    [onUnlocked, giveUp],
  )

  useEffect(() => {
    if (pin.length === 5 && !busy) void submit(pin)
  }, [pin, busy, submit])

  const left = MAX_LOCAL_FAILS - localFails()

  return (
    <div className="m-lock">
      <div className="m-lock-top">
        <div className="m-lock-badge">{me.initials}</div>

        <div>
          <div className="m-lock-title">{me.greeting}</div>
          {me.name && <div className="m-lock-title">{me.name}</div>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="m-lock-sub">
            {busy ? 'Проверяем…' : left < MAX_LOCAL_FAILS ? `Введите код · осталось попыток: ${left}` : 'Введите код'}
          </div>
          <div className="m-pips">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className={`m-pip ${i < pin.length ? 'on' : ''} ${error ? 'bad' : ''}`} />
            ))}
          </div>
          <div className="m-lock-err">{error ?? ' '}</div>
        </div>
      </div>

      <div className="m-lock-gap" />

      <Keypad
        onDigit={(d) => {
          if (busy) return
          setError(null)
          setPin((p) => (p.length >= 5 ? p : p + d))
        }}
        onErase={() => setPin((p) => p.slice(0, -1))}
        leftLabel="Не могу войти"
        onLeft={onForget}
      />
    </div>
  )
}

/** Предложение задать код сразу после обычного входа. */
export function PinSetup({ fullName, onDone, onSkip }: {
  fullName: string
  onDone: () => void
  onSkip: () => void
}) {
  const [pin, setPin] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const stage = pin.length < 5 ? 'first' : 'repeat'
  const current = stage === 'first' ? pin : again

  useEffect(() => {
    if (again.length !== 5) return
    if (again !== pin) {
      setError('Коды не совпали, начните заново')
      setPin('')
      setAgain('')
      return
    }
    setBusy(true)
    api
      .setPin(deviceUid(), pin, navigator.userAgent.slice(0, 60))
      .then(async () => {
        // Локальная проверочная запись нужна, чтобы войти без связи и чтобы
        // было чем расшифровать кэш доступов.
        await setLocalPin(pin)
        rememberPinSet(true)
        rememberWho(fullName)
        onDone()
      })
      .catch((e: any) => {
        // Сервер отвергает слишком простые коды — сообщение приходит от него.
        setError(e?.message ?? 'Не удалось сохранить код')
        setPin('')
        setAgain('')
      })
      .finally(() => setBusy(false))
  }, [again, pin, fullName, onDone])

  return (
    <div className="m-lock">
      <div className="m-lock-top">
        <div className="m-lock-badge">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="10" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <div>
          <div className="m-lock-title">Код быстрого</div>
          <div className="m-lock-title">входа</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="m-lock-sub">
            {busy ? 'Сохраняем…' : stage === 'first' ? 'Придумайте пять цифр' : 'Повторите код'}
          </div>
          <div className="m-pips">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className={`m-pip ${i < current.length ? 'on' : ''} ${error ? 'bad' : ''}`} />
            ))}
          </div>
          <div className="m-lock-err">{error ?? ' '}</div>
        </div>
      </div>

      <div className="m-lock-gap" />

      <Keypad
        onDigit={(d) => {
          if (busy) return
          setError(null)
          if (stage === 'first') setPin((p) => (p.length >= 5 ? p : p + d))
          else setAgain((p) => (p.length >= 5 ? p : p + d))
        }}
        onErase={() => {
          if (stage === 'first') setPin((p) => p.slice(0, -1))
          else setAgain((p) => p.slice(0, -1))
        }}
        leftLabel="Позже"
        onLeft={onSkip}
      />
    </div>
  )
}
